import { Button, makeStyles } from '@fluentui/react-components';
import { DismissRegular, ErrorCircleFilled } from '@fluentui/react-icons';
import React from 'react';
import { MessageBar, MessageBarActions, MessageBarBody } from './ScrollableMessageBar';

const useStyles = makeStyles({
  successText: { fontWeight: '600', color: '#107C10' },
  errorText: { color: '#6E0811' },
});

export type SettingsBannerIntent = 'success' | 'error';

export interface SettingsBannerProps {
  intent: SettingsBannerIntent;
  message: React.ReactNode;
  onDismiss: () => void;
  barTestId: string;
  dismissTestId: string;
}

/**
 * A dismissible success/error banner used by the settings pages. The success
 * variant is semibold green; the error variant is red (#6E0811) with a rounded
 * intent icon and matching border. Both expose an X dismiss button.
 */
export const SettingsBanner: React.FC<SettingsBannerProps> = ({
  intent, message, onDismiss, barTestId, dismissTestId,
}) => {
  const styles = useStyles();
  const config = {
    success: { text: styles.successText, icon: undefined, style: undefined },
    error: {
      text: styles.errorText,
      icon: { children: <ErrorCircleFilled />, className: styles.errorText },
      style: { borderColor: '#6E0811' },
    },
  }[intent];

  return (
    <MessageBar intent={intent} data-testid={barTestId} icon={config.icon} style={config.style}>
      <MessageBarBody className={config.text}>{message}</MessageBarBody>
      <MessageBarActions
        containerAction={
          <Button aria-label="dismiss" appearance="transparent" className={config.text}
            icon={<DismissRegular />} onClick={onDismiss} data-testid={dismissTestId} />
        }
      />
    </MessageBar>
  );
};
